import { Box, Dialog, DialogContent, Divider, Grid, Button, Typography } from '@mui/material';
import React, { forwardRef, memo, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import StockChartGraph from '../../components/common/StockChartGraph';
import consts from '../../consts';
import { usePrerequisites } from '../../contexts/Prerequisites';
import { useSetState } from '../../hooks';
import { useSterilizerCycleInfo } from './Context';

const TrendsLineChart = forwardRef((props, ref) => {
	const [searchParams] = useSearchParams();
	const [{ strings }] = usePrerequisites();
	const { data, filters, onPhasesFilter, openChartPopUp } = useSterilizerCycleInfo()
	const { STEAMEUSTERILIZER, AMSCOEUSTERILIZER, NONSTERISSTEAM, NONSTERISHP } = consts.EquipmentFamilyType;
	const isSteam = data?.EquipmentTypeId === consts.EquipmentType.STEAMSTERILIZER || Boolean(searchParams.get('isSteam'));
	const isSteamEU =
		[AMSCOEUSTERILIZER, STEAMEUSTERILIZER].includes(data?.EquipmentFamilyId) || Boolean(searchParams.get('IsSteamEU'));
	const isPhasesDataAvailable = !!(data.ChartDetail && data?.ChartDetail?.isPhasesDataAvailable);
	const [state, setState] = useSetState({ dialoOpen: false })
	const [trendlinesConfigs, chartData] = useMemo(() => {
		let linesData = null;
		let cData = {};
		const lines = [];
		if (
			!data ||
			typeof data !== 'object' ||
			!isPhasesDataAvailable ||
			[NONSTERISSTEAM, NONSTERISHP].includes(data?.EquipmentFamilyId)
		) {
			return [];
		} else {
			linesData = {
				isPressureExist: !!(
					(isSteamEU && data.GraphMinPhase1Low && data.GraphMaxPhase1High) ||
					(isSteam && (data.MinSterilizePressure || data.MaxSterilizePressure)) ||
					(!isSteam &&
						(data.PressureRise1Actual ||
							data.PressureRise2Actual ||
							data.PressureRise3Actual ||
							data.PressureRise4Actual))
				),
				isTemperatureExist: !!(
					(isSteamEU && data.GraphMinPhase1Low && data.GraphMinPhase1High) ||
					(isSteam && (data.MinSterilizeTemp || data.MaxSterilizeTemp)) ||
					(!isSteam && (data.TemperatureActualMin || data.TemperatureActualMax))
				),
				minPressure: isSteam || isSteamEU ? data.SterilizeMinPressure : data.PressureRiseMetricMin,
				maxPressure: isSteam || isSteamEU ? data.SterilizeMaxPressure : data.PressureRiseMetricMax,
				minTemparature: isSteam || isSteamEU ? data.SterilizeMinTemp : data.TemperatureMetricMin,
				maxTempurature: isSteam || isSteamEU ? data.SterilizeMaxTemp : data.TemperatureMetricMax,
				graphMinPhase1Low: isSteamEU ? data.GraphMinPhase1Low : null,
				graphMaxPhase1High: isSteamEU ? data.GraphMaxPhase1High : null,
				graphMinPhase2Low: isSteamEU ? data.GraphMinPhase2Low : null,
				graphMaxPhase2High: isSteamEU ? data.GraphMaxPhase2High : null,
				IsSteamEU: isSteamEU
			};
			if (data?.ChartDetail) {
				const dataEntry = Object.entries(data?.ChartDetail).find(
					([key]) => key.split('_')[0] === 'LEAKTEST' || key === 'LEAKTEST'
				);
				if (dataEntry?.length === 2) {
					cData = dataEntry[1];
				}
			}

			if (linesData.IsSteamEU && linesData.graphMinPhase1Low) {
				lines.push({
					startvalue: linesData.graphMinPhase1Low,
					color: '#ff0000',
					displayvalue: 'Min Graph 1 Low',
					showOnTop: true,
					toolText: `Min Graph 1 Low: ${linesData.graphMinPhase1Low}`,
					parentYAxis: 'p'
				});
			}
			if (linesData.IsSteamEU && linesData.graphMaxPhase1High) {
				lines.push({
					startvalue: linesData.graphMaxPhase1High,
					color: '#ff0000',
					displayvalue: 'Max Graph 1 High',
					toolText: `Max Graph 1 High: ${linesData.graphMaxPhase1High}`,
					parentYAxis: 'p',
					showOnTop: true
				});
			}
			if (linesData.IsSteamEU && linesData.graphMinPhase2Low) {
				lines.push({
					startvalue: linesData.graphMinPhase2Low,
					color: '#3572b0',
					displayvalue: 'Min Graph 2 Low',
					showOnTop: true,
					toolText: `Min Graph 2 Low: ${linesData.graphMinPhase2Low}`,
					parentYAxis: 'p'
				});
			}
			if (linesData.IsSteamEU && linesData.graphMaxPhase2High) {
				lines.push({
					startvalue: linesData.graphMaxPhase2High,
					color: '#3572b0',
					displayvalue: 'Max Graph 2 High',
					showOnTop: true,
					toolText: `Max Graph 2 High: ${linesData.graphMaxPhase2High}`,
					parentYAxis: 'p'
				});
			}
			if (linesData.isPressureExist) {
				if (linesData.minPressure) {
					lines.push({
						startvalue: linesData.minPressure,
						color: '#06af72',
						displayvalue: 'Min Pressure',
						toolText: `Min Pressure: ${linesData.minPressure}`,
						parentYAxis: 'p',
						showOnTop: true
					});
				}
				if (linesData.maxPressure) {
					lines.push({
						startvalue: linesData.maxPressure,
						color: '#06af72',
						displayvalue: 'Max Pressure',
						toolText: `Max Pressure ${linesData.maxPressure}`,
						parentYAxis: 'p',
						showOnTop: true
					});
				}
			}
			if (linesData.isTemperatureExist) {
				if (linesData.minTemparature) {
					lines.push({
						startvalue: linesData.minTemparature,
						color: '#e8b416',
						displayvalue: 'Min Temperature',
						showOnTop: true,
						toolText: `Min Temperature ${linesData.minTemparature}`,
						parentYAxis: 's'
					});
				}
				if (linesData.maxTempurature) {
					lines.push({
						startvalue: linesData.maxTempurature,
						color: '#e8b416',
						displayvalue: ' Max Temperature ',
						showOnTop: true,
						toolText: `Max Temperature ${linesData.maxTempurature}`,
						parentYAxis: 's'
					});
				}
			}
		}


		return [
			{
				line: lines
			},
			cData
		];
	}, [NONSTERISHP, NONSTERISSTEAM, data, isSteam, isSteamEU, isPhasesDataAvailable]);

	const extraControls = useMemo(() => {
		const controls = [];
		if (!state.dialogOpen) {
			controls.push({
				text: strings.OpenInNewWindow,
				title: strings.OpenInNewWindow,
				onClick: () => setState({ dialogOpen: true })
			});
		}
		else {
			controls.push({
				title: strings.Close,

				Component: () =>
					<Button
						onClick={() => setState({ dialogOpen: false })}
						title={strings.Close}
						color="error"
						variant="contained"
						size="small"
						sx={{
							px: 1,
							fontSize: 14,
							textTransform: 'uppercase',
							fontWeight: 'bold',
						}}
					>
						{strings.Close}
					</Button>

			});
		}
		return controls;
	}, [setState, state.dialogOpen, strings.OpenInNewWindow]);
	return (
		<Box>
			{!isPhasesDataAvailable && (
				<Box className="print-clear stock-chart-main-div" height={5} paddingTop={5}>
					<Typography variant="subtitle1" className="text-center stock-chart">
						{strings.NoChartDataAvailableForCycle}
					</Typography>
					<Divider />
				</Box>
			)}
			{isPhasesDataAvailable && (
				<Box className="print-clear stock-chart-main-div">
					<Box className=" print-clear">
						<Box className="text-center stock-chart">
							<StockChartGraph
								title=""
								iconWidth="10%"
								data={chartData}
								chartDetail={data?.ChartDetail}
								isStart={true}
								trendsLinesConfigs={trendlinesConfigs}
								trendlinesBgColor={'#000000'}
								onPhasesFilter={onPhasesFilter}
								openChartPopUp={openChartPopUp}
								filters={filters}
								ref={ref}
								extraControls={extraControls}
							/>
						</Box>
					</Box>
					{state.dialogOpen && (
						<Dialog open onClose={() => setState({ dialogOpen: false })} fullScreen sx={{ zIndex: theme => theme.zIndex.tooltip }}>
							<DialogContent>
								<Grid container size={12}>
									<Grid size={12} item alignItems="center" paddingTop={2} paddingBottom={2}>
										<StockChartGraph
											title=""
											iconWidth="10%"
											data={chartData}
											chartDetail={data?.ChartDetail}
											isStart={true}
											trendsLinesConfigs={trendlinesConfigs}
											trendlinesBgColor={'#000000'}
											onPhasesFilter={onPhasesFilter}
											openChartPopUp={openChartPopUp}
											filters={filters}
											extraControls={extraControls}
										/>
									</Grid>
								</Grid>
							</DialogContent>
						</Dialog>
					)}
				</Box>
			)}
		</Box>
	);
});

export default memo(TrendsLineChart);
